/**
 * Builds a single-line address without duplicating suburb/state/postcode.
 * If the base address already ends with postcode or contains suburb, it is treated as full and returned as-is.
 */
export function buildAddressLine(parts) {
    const address = (parts.address ?? "").trim();
    const suburb = (parts.suburb ?? "").trim();
    const state = (parts.state ?? "").trim();
    const postcode = (parts.postcode ?? "").trim();
    if (!address && !suburb && !state && !postcode)
        return "";
    const base = address || suburb || state || postcode;
    if (!base)
        return "";
    if (address) {
        if (postcode && address.endsWith(postcode))
            return address;
        if (suburb && address.includes(suburb))
            return address;
    }
    const tokens = [address, suburb, state, postcode].filter(Boolean);
    return tokens.join(", ");
}
/**
 * Returns full display address for a job (Street, Suburb, State, Postcode).
 * Prefer siteAddress when present (backend sends full address there when site exists);
 * else build from address parts; else use location; else fallback.
 */
export function formatJobAddress(job, fallback = "—") {
    const siteAddr = (job.siteAddress ?? "").trim();
    if (siteAddr)
        return siteAddr;
    const built = buildAddressLine({
        address: job.address ?? undefined,
        suburb: job.suburb ?? undefined,
        state: job.state ?? undefined,
        postcode: job.postcode ?? undefined,
    });
    if (built)
        return built;
    const loc = (job.location ?? "").trim();
    return loc || fallback;
}
/**
 * Returns full display address for a site. Builds from address, suburb, state, postcode.
 */
export function formatSiteAddress(site, fallback = "—") {
    const result = buildAddressLine({
        address: site.address,
        suburb: site.suburb,
        state: site.state,
        postcode: site.postcode,
    });
    return result || fallback;
}
/**
 * Returns full display address for a quote. Prefer address (often full) over siteAddress.
 */
export function formatQuoteAddress(quote, fallback = "—") {
    const addr = (quote.address ?? "").trim();
    if (addr && addr.includes(","))
        return addr;
    const siteAddr = (quote.siteAddress ?? "").trim();
    const suburb = (quote.suburb ?? "").trim();
    if (siteAddr && siteAddr.includes(","))
        return siteAddr;
    const parts = [addr || siteAddr, suburb].filter(Boolean);
    if (parts.length > 0)
        return parts.join(", ");
    return addr || siteAddr || fallback;
}
